export type PieSeed = { id: string; title: string; unit: string; total: number; parts: Array<{ name: string; value: number }> }
export type SeriesSeed = { id: string; title: string; chart: 'bar' | 'line'; unit: string; values: Array<{ name: string; value: number }> }
export type ComboSeed = { id: string; title: string; volumeUnit: string; priceUnit: string; values: Array<{ name: string; volume: number; price: number }> }
export type TableSeed = { id: string; title: string; rows: Array<{ name: string; revenue: number; cost: number; people: number }> }
export type PerformanceTableSeed = { id: string; title: string; rows: Array<{ name: string; below: number; standard: number; advanced: number }> }
export type WorkforceTableSeed = { id: string; title: string; rows: Array<{ name: string; femaleA: number; maleA: number; femaleB: number; maleB: number }> }
export type FrequencySeed = { id: string; title: string; unit: string; rows: Array<{ name: string; value: number }> }
export type DualSeriesSeed = { id: string; title: string; unit: string; years: string[]; firstName: string; secondName: string; first: number[]; second: number[] }
export type ShareComparisonSeed = { id: string; title: string; unit: string; firstLabel: string; secondLabel: string; firstTotal: number; secondTotal: number; parts: Array<{ name: string; firstShare: number; secondShare: number }> }
export type ProfitCurveSeed = { id: string; title: string; unit: string; unitPrice: number; points: Array<{ quantity: number; cost: number }> }
export type PricingSeed = { id: string; title: string; currency: string; taxRate: number; retailMarkup: number; exchangeRate: number; rows: Array<{ name: string; unitsPerKg: number; basePrice: number }> }
export type BudgetSeed = { id: string; title: string; total: number; unit: string; rows: Array<{ name: string; share: number }>; actualFood: number; utilitySavingRate: number }
export type TwinShareSeed = { id: string; title: string; unit: string; totalCount: number; totalRevenue: number; rows: Array<{ name: string; countShare: number; revenueShare: number }> }
export type RebalancedShareSeed = { id: string; title: string; unit: string; total: number; rows: Array<{ name: string; share: number }>; primaryChange: number; secondaryChange: number }
export type TimelineShareSeed = { id: string; title: string; years: string[]; categories: string[]; shares: number[][]; totalGrowth: number }
export type ConstrainedAllocationSeed = { id: string; title: string; total: number; rows: Array<{ name: string; value: number }>; unit: string }
export type DualMeasureSeed = { id: string; title: string; unitA: string; unitB: string; rows: Array<{ name: string; first: number; second: number }> }
export type ChangeRateSeed = { id: string; title: string; periods: string[]; current: number[]; declineRates: number[]; unit: string }
export type AgeTransitionSeed = { id: string; title: string; teams: Array<{ name: string; averages: number[]; entrantAges: number[] }> }
export type BranchStackSeed = { id: string; title: string; years: string[]; branches: string[]; values: number[][]; unit: string }
export type CompositionFlowSeed = { id: string; title: string; years: string[]; categories: string[]; shares: number[][]; firstTotal: number; finalTotal: number; unit: string }
export type SeafoodProductionSeed = { id: string; title: string; unit: string; years: string[]; capture: number[]; farming: number[] }
export type FactoryEmployeesSeed = { id: string; title: string; unit: string; years: string[]; factories: string[]; employees: number[][] }
export type RadarSeed = { id: string; title: string; subjects: string[]; criteria: string[]; scores: number[][] }
export type ScatterSeed = { id: string; title: string; xName: string; yName: string; xUnit: string; yUnit: string; points: Array<{ x: number; y: number }> }
export type BubbleSeed = { id: string; title: string; xName: string; yName: string; zName: string; xUnit: string; yUnit: string; zUnit: string; points: Array<{ x: number; y: number; z: number }> }
export type HeatmapSeed = { id: string; title: string; rows: string[]; columns: string[]; matrix: number[][] }

function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = Math.imul(31, hash) + str.charCodeAt(i) | 0;
  return hash;
}

function mulberry32(a: number) {
  return function() {
    let t = a += 0x6D2B79F5;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }
}

export class SeedGenerator {
  private rng: () => number;
  constructor(seedString: string) {
    this.rng = mulberry32(hashString(seedString));
  }
  
  randomInt(min: number, max: number): number {
    return Math.floor(this.rng() * (max - min + 1)) + min;
  }
}

export function generatePieSeed(id: string): PieSeed {
  const gen = new SeedGenerator(id);
  const contexts = [
    { title: 'Cơ cấu chi ngân sách dự án', unit: 'triệu đồng', items: ['Nghiên cứu', 'Thiết bị', 'Nhân sự', 'Vận hành', 'Khác'] },
    { title: 'Cơ cấu diện tích đất nông nghiệp', unit: 'ha', items: ['Lúa', 'Hoa màu', 'Cây ăn quả', 'Thủy sản', 'Khác'] },
    { title: 'Phân bổ năng lượng tái tạo', unit: 'triệu kWh', items: ['Mặt trời', 'Gió', 'Thủy điện', 'Sinh khối', 'Khác'] },
    { title: 'Cơ cấu doanh thu các mảng', unit: 'tỷ đồng', items: ['Dịch vụ', 'Sản phẩm', 'Bảo hành', 'Tư vấn', 'Khác'] },
  ];
  const context = contexts[gen.randomInt(0, contexts.length - 1)];
  const total = gen.randomInt(5, 50) * 100; // 500 to 5000
  
  const parts = [];
  let remaining = 100;
  for (let i = 0; i < 4; i++) {
    const maxVal = remaining - (4 - i) * 5;
    const minVal = 10;
    const val = gen.randomInt(minVal, maxVal);
    parts.push({ name: context.items[i], value: val });
    remaining -= val;
  }
  parts.push({ name: context.items[4], value: remaining });
  
  parts.sort((a, b) => b.value - a.value);
  if (parts[0].value === 50) {
    parts[0].value += 1;
    parts[parts.length - 1].value -= 1;
  }

  return { id, title: context.title, unit: context.unit, total, parts };
}

export function generateSeriesSeed(id: string): SeriesSeed {
  const gen = new SeedGenerator(id);
  const contexts = [
    { title: 'Sản lượng tiêu thụ vật liệu xây dựng', unit: 'nghìn tấn', chart: 'bar' as const },
    { title: 'Số lượng đăng ký kinh doanh mới', unit: 'doanh nghiệp', chart: 'line' as const },
    { title: 'Doanh thu thương mại điện tử', unit: 'tỷ đồng', chart: 'bar' as const },
    { title: 'Lượng khách sử dụng tàu điện', unit: 'nghìn lượt', chart: 'line' as const },
  ];
  const context = contexts[gen.randomInt(0, contexts.length - 1)];
  const startYear = gen.randomInt(2018, 2021);
  const values = [];
  let currentValue = gen.randomInt(50, 200) * 10; // Round numbers
  for (let i = 0; i < 4; i++) {
    values.push({ name: `${startYear + i}`, value: currentValue });
    currentValue += gen.randomInt(1, 5) * 10; // Strictly increasing to ensure last year is peak
  }
  return { id, ...context, values };
}

export function generateComboSeed(id: string): ComboSeed {
  const gen = new SeedGenerator(id);
  const contexts = [
    { title: 'Xuất khẩu nông sản: sản lượng và giá', volumeUnit: 'triệu tấn', priceUnit: 'USD/tấn' },
    { title: 'Tiêu thụ than: sản lượng và giá', volumeUnit: 'triệu tấn', priceUnit: 'USD/tấn' },
    { title: 'Xuất khẩu thuỷ sản: sản lượng và giá', volumeUnit: 'triệu tấn', priceUnit: 'USD/tấn' },
  ];
  const context = contexts[gen.randomInt(0, contexts.length - 1)];
  const startYear = gen.randomInt(2020, 2022);
  const values = [];
  let vol = gen.randomInt(10, 50) / 10; // 1.0 to 5.0
  let price = gen.randomInt(30, 100) * 10; // 300 to 1000
  for (let i = 0; i < 3; i++) {
    values.push({ name: `${startYear + i}`, volume: vol, price });
    vol = Number((vol + gen.randomInt(-5, 10) / 10).toFixed(1));
    if (vol < 0.5) vol = 0.5;
    price += gen.randomInt(-5, 10) * 10;
  }
  return { id, ...context, values };
}

export function generateTableSeed(id: string): TableSeed {
  const gen = new SeedGenerator(id);
  const contexts = [
    { title: 'Kết quả kinh doanh 3 chi nhánh', names: ['Chi nhánh A', 'Chi nhánh B', 'Chi nhánh C'] },
    { title: 'Hiệu quả 3 đội bán hàng', names: ['Đội Alpha', 'Đội Beta', 'Đội Gamma'] },
    { title: 'Sản xuất 3 phân xưởng', names: ['Phân xưởng 1', 'Phân xưởng 2', 'Phân xưởng 3'] },
  ];
  const context = contexts[gen.randomInt(0, contexts.length - 1)];
  const rows = context.names.map(name => {
    const revenue = gen.randomInt(20, 80) * 10; // 200 to 800
    // cost is between 60% and 85% of revenue, force integer
    const costRatio = gen.randomInt(60, 85) / 100;
    const cost = Math.floor(revenue * costRatio);
    const people = gen.randomInt(10, 40);
    return { name, revenue, cost, people };
  });
  return { id, title: context.title, rows };
}

/** Original score-band tables; values are seed-derived and never taken from an exam source. */
export function generatePerformanceTableSeed(id: string): PerformanceTableSeed {
  const gen = new SeedGenerator(id)
  const names = ['Lớp A', 'Lớp B', 'Lớp C', 'Lớp D', 'Lớp E']
  let rows: PerformanceTableSeed['rows'] = []
  for (let attempt = 0; attempt < 24; attempt += 1) {
    rows = names.map(name => ({
      name,
      below: gen.randomInt(10, 35),
      standard: gen.randomInt(45, 85),
      advanced: gen.randomInt(5, 12) * 5,
    }))
    const rates = rows.map(row => row.below / (row.below + row.standard + row.advanced))
    const max = Math.max(...rates)
    if (rates.filter(rate => Math.abs(rate - max) < 1e-10).length === 1) break
  }
  return { id, title: 'Kết quả đánh giá năng lực của năm lớp', rows }
}

/** Employment-style two-cohort tables with fresh labels and integers per seed. */
export function generateWorkforceTableSeed(id: string): WorkforceTableSeed {
  const gen = new SeedGenerator(id)
  const names = ['Giảng dạy', 'Phân tích dữ liệu', 'Phát triển phần mềm', 'Dịch vụ tài chính']
  let rows: WorkforceTableSeed['rows'] = []
  for (let attempt = 0; attempt < 24; attempt += 1) {
    rows = names.map(name => ({
      name,
      femaleA: gen.randomInt(8, 30),
      maleA: gen.randomInt(35, 95),
      femaleB: gen.randomInt(8, 30),
      maleB: gen.randomInt(35, 95),
    }))
    const rates = rows.map(row => (row.femaleA + row.femaleB) / (row.femaleA + row.femaleB + row.maleA + row.maleB))
    const max = Math.max(...rates)
    if (rates.filter(rate => Math.abs(rate - max) < 1e-10).length === 1) break
  }
  return { id, title: 'Khảo sát việc làm của hai khóa tốt nghiệp', rows }
}

export function generateFrequencySeed(id: string): FrequencySeed {
  const gen = new SeedGenerator(id)
  const labels = ['Không ghi nhận', 'Mức nhẹ', 'Mức vừa', 'Mức rõ', 'Mức cao', 'Mức rất cao']
  const rows = labels.map((name, index) => ({ name, value: index === 0 ? gen.randomInt(180, 360) : gen.randomInt(8, 170 - index * 16) }))
  return { id, title: 'Phân bố mức phản hồi trong khảo sát trải nghiệm', unit: 'người', rows }
}

export function generateDualSeriesSeed(id: string): DualSeriesSeed {
  const gen = new SeedGenerator(id)
  const years = Array.from({ length: 6 }, (_, index) => `${2020 + index}`)
  let first: number[] = []
  let second: number[] = []
  for (let attempt = 0; attempt < 24; attempt += 1) {
    first = years.map((_, index) => 75 + index * gen.randomInt(8, 16) + gen.randomInt(-12, 12))
    second = years.map((_, index) => 50 + index * gen.randomInt(7, 15) + gen.randomInt(-10, 10))
    const gaps = first.map((value, index) => Math.abs(value - second[index]))
    const increases = first.slice(1).map((value, index) => value - first[index])
    const minGap = Math.min(...gaps)
    const maxIncrease = Math.max(...increases)
    if (gaps.filter(value => value === minGap).length === 1 && increases.filter(value => value === maxIncrease).length === 1) break
  }
  return { id, title: 'Sản lượng tiêu thụ của hai nhãn hàng', unit: 'nghìn thiết bị', years, firstName: 'Nhãn A', secondName: 'Nhãn B', first, second }
}

export function generateShareComparisonSeed(id: string): ShareComparisonSeed {
  const gen = new SeedGenerator(id)
  const firstShare = [gen.randomInt(22, 38), gen.randomInt(20, 32)]
  firstShare.push(100 - firstShare[0] - firstShare[1])
  const secondShare = [gen.randomInt(22, 38), gen.randomInt(20, 32)]
  secondShare.push(100 - secondShare[0] - secondShare[1])
  return {
    id, title: 'Cơ cấu sản lượng của ba nhóm nguồn', unit: 'triệu đơn vị', firstLabel: 'Năm đầu', secondLabel: 'Năm cuối',
    firstTotal: gen.randomInt(35, 65) * 10, secondTotal: gen.randomInt(95, 160) * 10,
    parts: ['Nguồn A', 'Nguồn B', 'Nguồn C'].map((name, index) => ({ name, firstShare: firstShare[index], secondShare: secondShare[index] })),
  }
}

export function generateProfitCurveSeed(id: string): ProfitCurveSeed {
  const gen = new SeedGenerator(id)
  const unitPrice = gen.randomInt(28, 46)
  const quantities = [50, 100, 150, 200, 250, 300]
  const fixedCost = gen.randomInt(300, 750)
  const variable = gen.randomInt(9, Math.min(16, unitPrice - 12))
  const points = quantities.map(quantity => ({ quantity, cost: fixedCost + variable * quantity + Math.floor(quantity * quantity / gen.randomInt(130, 210)) }))
  return { id, title: 'Chi phí sản xuất theo sản lượng trong một ngày', unit: 'USD', unitPrice, points }
}

export function generatePricingSeed(id: string): PricingSeed {
  const gen = new SeedGenerator(id)
  const currency = 'EUR'
  const rows = [
    { name: 'Hạng chọn lọc', unitsPerKg: 40, basePrice: gen.randomInt(230, 290) },
    { name: 'Hạng tiêu chuẩn', unitsPerKg: 55, basePrice: gen.randomInt(170, 230) },
    { name: 'Hạng phổ thông', unitsPerKg: 75, basePrice: gen.randomInt(105, 165) },
    { name: 'Hạng tiết kiệm', unitsPerKg: 90, basePrice: gen.randomInt(75, 110) },
  ]
  return { id, title: 'Bảng giá hạt dinh dưỡng theo hạng', currency, taxRate: 8, retailMarkup: 15, exchangeRate: gen.randomInt(25000, 27000), rows }
}

export function generateBudgetSeed(id: string): BudgetSeed {
  const gen = new SeedGenerator(id)
  const education = gen.randomInt(18, 28)
  const utilities = gen.randomInt(12, 20)
  const other = gen.randomInt(14, 22)
  const remaining = 100 - education - utilities - other
  const investment = Math.floor(remaining * 3 / 7)
  const food = remaining - investment
  const total = gen.randomInt(24, 48) * 1_000_000
  const utilityAmount = total * utilities / 100
  const utilitySavingRate = gen.randomInt(15, 35)
  return { id, title: 'Kế hoạch chi tiêu tháng của một hộ gia đình', total, unit: 'đồng', rows: [{ name: 'Giáo dục', share: education }, { name: 'Điện nước', share: utilities }, { name: 'Thực phẩm', share: food }, { name: 'Đầu tư', share: investment }, { name: 'Khác', share: other }], actualFood: total * food / 100 * (1 + gen.randomInt(-12, 18) / 100), utilitySavingRate }
}

/** Same entities measured against two different totals (volume and value). */
export function generateTwinShareSeed(id: string): TwinShareSeed {
  const gen = new SeedGenerator(id)
  const names = ['Đơn vị A', 'Đơn vị B', 'Đơn vị C', 'Đơn vị D', 'Đơn vị E']
  const countShares = [gen.randomInt(14, 28), gen.randomInt(12, 24), gen.randomInt(10, 20), gen.randomInt(8, 16)]
  countShares.push(100 - countShares.reduce((sum, value) => sum + value, 0))
  const revenueShares = [gen.randomInt(10, 24), gen.randomInt(14, 30), gen.randomInt(10, 22), gen.randomInt(8, 18)]
  revenueShares.push(100 - revenueShares.reduce((sum, value) => sum + value, 0))
  return { id, title: 'Cơ cấu khối lượng và doanh thu của năm đơn vị', unit: 'triệu đồng', totalCount: gen.randomInt(120, 260) * 10, totalRevenue: gen.randomInt(80, 180) * 1_000_000, rows: names.map((name, index) => ({ name, countShare: countShares[index], revenueShare: revenueShares[index] })) }
}

/** Six-part allocation with independent changes, for original rebalancing questions. */
export function generateRebalancedShareSeed(id: string): RebalancedShareSeed {
  const gen = new SeedGenerator(id)
  const values = [gen.randomInt(16, 28), gen.randomInt(12, 20), gen.randomInt(10, 18), gen.randomInt(8, 15), gen.randomInt(6, 12)]
  values.push(100 - values.reduce((sum, value) => sum + value, 0))
  return {
    id, title: 'Cơ cấu nguồn lực của sáu nhóm hoạt động', unit: 'triệu đồng', total: gen.randomInt(180, 420) * 1_000_000,
    rows: ['Nhóm A', 'Nhóm B', 'Nhóm C', 'Nhóm D', 'Nhóm E', 'Nhóm F'].map((name, index) => ({ name, share: values[index] })),
    primaryChange: gen.randomInt(12, 35), secondaryChange: -gen.randomInt(8, 22),
  }
}

export function generateTimelineShareSeed(id: string): TimelineShareSeed {
  const gen = new SeedGenerator(id)
  const years = ['Năm 1', 'Năm 2', 'Năm 3']
  const services = [gen.randomInt(39, 48), gen.randomInt(39, 48), gen.randomInt(39, 48)]
  const industry = [gen.randomInt(28, 38), gen.randomInt(28, 38), gen.randomInt(28, 38)]
  const shares = years.map((_, index) => [100 - services[index] - industry[index], industry[index], services[index]])
  return { id, title: 'Cơ cấu giá trị của ba khu vực hoạt động theo thời gian', years, categories: ['Khu vực sơ cấp', 'Khu vực chế biến', 'Khu vực dịch vụ'], shares, totalGrowth: gen.randomInt(8, 18) }
}

export function generateConstrainedAllocationSeed(id: string): ConstrainedAllocationSeed {
  const gen = new SeedGenerator(id)
  const craft = gen.randomInt(900, 1400)
  const tech = craft + gen.randomInt(1800, 3200)
  const marine = tech + gen.randomInt(400, 900)
  const mechanical = craft * 4
  const electrical = gen.randomInt(1100, 1800)
  return { id, title: 'Phân bổ người học theo năm lĩnh vực chuyên môn', total: craft + tech + marine + mechanical + electrical, unit: 'người', rows: [{ name: 'Dệt thiết kế', value: craft }, { name: 'Công nghệ số', value: tech }, { name: 'Hàng hải', value: marine }, { name: 'Cơ khí', value: mechanical }, { name: 'Điện ứng dụng', value: electrical }] }
}

export function generateDualMeasureSeed(id: string): DualMeasureSeed {
  const gen = new SeedGenerator(id)
  const names = ['Vùng Bắc', 'Vùng Trung', 'Vùng Đông', 'Vùng Tây', 'Vùng Nam']
  const rows = names.map((name, index) => ({ name, first: gen.randomInt(8, 46), second: gen.randomInt(180, 4200) }))
  return { id, title: 'Quy mô không gian và dân số của năm vùng', unitA: 'triệu km²', unitB: 'triệu người', rows }
}

/** Current values paired with their decline rates, used to practise reverse percentage reasoning. */
export function generateChangeRateSeed(id: string): ChangeRateSeed {
  const gen = new SeedGenerator(id)
  const periods = ['Kỳ 1', 'Kỳ 2', 'Kỳ 3', 'Kỳ 4', 'Kỳ 5', 'Kỳ 6']
  const current = periods.map(() => gen.randomInt(65, 185) * 10)
  const declineRates = periods.map(() => gen.randomInt(3, 15))
  return { id, title: 'Quy mô người cần hỗ trợ theo từng kỳ và mức giảm so với kỳ trước', periods, current, declineRates, unit: 'nghìn người' }
}

/** Three-person teams: one 60-year-old leaves and one new member joins each year. */
export function generateAgeTransitionSeed(id: string): AgeTransitionSeed {
  const gen = new SeedGenerator(id)
  const names = ['Nhóm Điều phối', 'Nhóm Kỹ thuật', 'Nhóm Phân tích', 'Nhóm Vận hành']
  const teams = names.map(name => {
    const entrantAges = [gen.randomInt(22, 34), gen.randomInt(22, 34), gen.randomInt(22, 34)]
    const averages = [gen.randomInt(33, 48)]
    for (let year = 0; year < entrantAges.length; year += 1) {
      const nextTotal = averages[year] * 3 - 60 + 2 + entrantAges[year]
      averages.push(Number((nextTotal / 3).toFixed(2)))
    }
    return { name, averages, entrantAges }
  })
  return { id, title: 'Tuổi trung bình của các nhóm qua bốn năm thay nhân sự', teams }
}

/** Stacked branch totals; values are seed-created and never copied from a source chart. */
export function generateBranchStackSeed(id: string): BranchStackSeed {
  const gen = new SeedGenerator(id)
  const years = ['Năm 1', 'Năm 2', 'Năm 3']
  const branches = ['Trung tâm Bắc', 'Trung tâm Trung', 'Trung tâm Nam']
  const values = branches.map((_, branch) => years.map((_, year) => gen.randomInt(55, 165) + branch * 17 + year * gen.randomInt(-8, 20)))
  return { id, title: 'Quy mô nhân sự theo trung tâm trong ba năm', years, branches, values, unit: 'người' }
}

/** A 100%-composition timeline with an independently changing total at the endpoints. */
export function generateCompositionFlowSeed(id: string): CompositionFlowSeed {
  const gen = new SeedGenerator(id)
  const years = ['Năm 1', 'Năm 2', 'Năm 3', 'Năm 4', 'Năm 5']
  const categories = ['Nông nghiệp – tài nguyên', 'Chế biến – tiêu dùng', 'Công nghệ – hạ tầng']
  let shares: number[][] = []
  for (let attempt = 0; attempt < 24; attempt += 1) {
    shares = years.map(() => {
      const first = gen.randomInt(20, 43)
      const second = gen.randomInt(25, 47)
      return [first, second, 100 - first - second]
    })
    const changes = categories.map((_, index) => Math.abs(shares[4][index] - shares[0][index]))
    if (changes.filter(change => change === Math.max(...changes)).length === 1) break
  }
  return { id, title: 'Cơ cấu giá trị hoạt động theo nhóm ngành trong năm giai đoạn', years, categories, shares, firstTotal: gen.randomInt(280, 450) * 10, finalTotal: gen.randomInt(520, 800) * 10, unit: 'tỷ đồng' }
}

export function generateSeafoodProductionSeed(id: string): SeafoodProductionSeed {
  const gen = new SeedGenerator(id)
  const baseYear = gen.randomInt(1985, 2010)
  const years = [baseYear, baseYear + 4, baseYear + 8, baseYear + 12].map(String)
  
  // Base values somewhat close to original but randomized
  const capture = [
    gen.randomInt(650, 800) + gen.randomInt(1, 9) / 10,
    gen.randomInt(1000, 1200) + gen.randomInt(1, 9) / 10,
    gen.randomInt(1250, 1450),
    gen.randomInt(1700, 1900) + gen.randomInt(1, 9) / 10,
  ]
  const farming = [
    gen.randomInt(120, 200) + gen.randomInt(1, 9) / 10,
    gen.randomInt(300, 400) + gen.randomInt(1, 9) / 10,
    gen.randomInt(400, 500),
    gen.randomInt(750, 950) + gen.randomInt(1, 9) / 10,
  ]
  
  return { id, title: 'SẢN LƯỢNG KHAI THÁC VÀ NUÔI TRỒNG THỦY SẢN (NGHÌN TẤN)', unit: 'nghìn tấn', years, capture, farming }
}

export function generateFactoryEmployeesSeed(id: string): FactoryEmployeesSeed {
  const gen = new SeedGenerator(id)
  const baseYear = gen.randomInt(2018, 2023)
  const years = [baseYear, baseYear + 1, baseYear + 2].map(String)
  const factories = ['Xưởng A', 'Xưởng B', 'Xưởng C']
  
  // Base values for A, B, C for 3 years, randomized in tens
  const employees = [
    // Year 1 (2020)
    [gen.randomInt(160, 220) * 10, gen.randomInt(160, 220) * 10, gen.randomInt(160, 220) * 10],
    // Year 2 (2021)
    [gen.randomInt(180, 240) * 10, gen.randomInt(180, 240) * 10, gen.randomInt(170, 230) * 10],
    // Year 3 (2022)
    [gen.randomInt(150, 200) * 10, gen.randomInt(170, 210) * 10, gen.randomInt(160, 210) * 10],
  ]
  
  return { id, title: 'SỐ LƯỢNG NHÂN VIÊN TRONG 3 XƯỞNG QUA CÁC NĂM', unit: 'người', years, factories, employees }
}
export function generateRadarSeed(id: string): RadarSeed {
  const gen = new SeedGenerator(id)
  const subjects = ['Sản phẩm A', 'Sản phẩm B']
  const criteria = ['Giá cả', 'Chất lượng', 'Độ bền', 'Thẩm mỹ', 'Bảo hành', 'Tiện ích']
  
  const scores = subjects.map(() => {
    return criteria.map(() => gen.randomInt(5, 10))
  })
  
  // Ensure they don't have the exact same total score to make questions interesting
  let sum0 = scores[0].reduce((a, b) => a + b, 0)
  let sum1 = scores[1].reduce((a, b) => a + b, 0)
  if (sum0 === sum1) {
    scores[0][0] = Math.min(10, scores[0][0] + 1)
  }

  return { id, title: 'Đánh giá mức độ hài lòng của khách hàng trên thang điểm 10', subjects, criteria, scores }
}

export function generateScatterSeed(id: string): ScatterSeed {
  const gen = new SeedGenerator(id)
  const count = gen.randomInt(15, 25)
  const points = []
  
  // Generate points with a positive correlation
  for (let i = 0; i < count; i++) {
    const x = gen.randomInt(1, 10) * 10 // e.g. 10 to 100
    // y = 2*x + noise
    const y = x * 2 + gen.randomInt(-20, 20)
    points.push({ x, y: Math.max(0, y) })
  }
  
  return { id, title: 'Mối liên hệ giữa Chi phí quảng cáo và Doanh thu', xName: 'Chi phí quảng cáo', yName: 'Doanh thu', xUnit: 'Triệu VNĐ', yUnit: 'Triệu VNĐ', points }
}

export function generateBubbleSeed(id: string): BubbleSeed {
  const gen = new SeedGenerator(id)
  const count = gen.randomInt(10, 15)
  const points = []
  
  for (let i = 0; i < count; i++) {
    const x = gen.randomInt(10, 100) // Life expectancy or something
    const y = gen.randomInt(1000, 50000) // GDP per capita
    const z = gen.randomInt(1, 100) * 10 // Population or volume
    points.push({ x, y, z })
  }
  
  return { id, title: 'Tuổi thọ, Thu nhập và Dân số các quốc gia', xName: 'Tuổi thọ', yName: 'Thu nhập bình quân', zName: 'Dân số', xUnit: 'Tuổi', yUnit: 'USD', zUnit: 'Triệu người', points }
}

export function generateHeatmapSeed(id: string): HeatmapSeed {
  const gen = new SeedGenerator(id)
  const rows = ['Khu vực A', 'Khu vực B', 'Khu vực C', 'Khu vực D']
  const columns = ['Quý 1', 'Quý 2', 'Quý 3', 'Quý 4']
  const matrix = []
  
  for (let r = 0; r < rows.length; r++) {
    const rowData = []
    for (let c = 0; c < columns.length; c++) {
      rowData.push(gen.randomInt(100, 900))
    }
    matrix.push(rowData)
  }
  
  return { id, title: 'Bản đồ nhiệt: Mật độ lượng mưa (mm) theo khu vực và thời gian', rows, columns, matrix }
}
